import React, { useState } from 'react';
import { Controller, Control, FieldErrors, FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayRemove, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { TrashIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers, EDUCATION_STATUS_OPTIONS } from '@/features/Collaborators/Models/collaborator';
import { RequiredIndicator } from '@/components/shared';
import { CollaboratorFormData, FormEducationEntry } from '@/features/Collaborators/Edit/Types';

interface EducationFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors?: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
    fields: FieldArrayWithId<CollaboratorFormData, "educations", "id">[];
    append: UseFieldArrayAppend<CollaboratorFormData, "educations">;
    remove: UseFieldArrayRemove;
}

const educationTypeOptions = ['Ensino Médio', 'Graduação', 'Pós-graduação', 'Mestrado', 'Doutorado'];

type EducationFieldName = 'type' | 'course' | 'status' | 'institution' | 'startDate' | 'endDate';

// Para o RH o que importa é tipo, curso e situação; instituição e datas são opcionais.
const educationFields: {
    name: EducationFieldName;
    label: string;
    kind: 'select' | 'text' | 'date';
    options?: readonly string[];
    placeholder?: string;
    required?: boolean;
}[] = [
    { name: 'type', label: 'Tipo', kind: 'select', options: educationTypeOptions, required: true },
    { name: 'course', label: 'Curso', kind: 'text', placeholder: 'Nome do curso' },
    { name: 'status', label: 'Status', kind: 'select', options: EDUCATION_STATUS_OPTIONS, placeholder: 'Selecione' },
    { name: 'institution', label: 'Instituição', kind: 'text', placeholder: 'Nome da instituição' },
    { name: 'startDate', label: 'Início', kind: 'date' },
    { name: 'endDate', label: 'Fim', kind: 'date' },
];

const inputClass = "w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm";

const emptyEducation = (): FormEducationEntry => ({
    type: 'Graduação',
    course: '',
    status: '',
    institution: '',
    startDate: '',
    endDate: ''
});

interface EducationInputProps {
    id?: string;
    config: typeof educationFields[number];
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    disabled: boolean;
}

const EducationInput: React.FC<EducationInputProps> = ({ id, config, value, onChange, onBlur, disabled }) => {
    if (config.kind === 'select') {
        return (
            <select id={id} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} disabled={disabled} className={inputClass}>
                {config.placeholder && <option value="">{config.placeholder}</option>}
                {config.options?.map(option => (
                    <option key={option} value={option}>{option}</option>
                ))}
            </select>
        );
    }
    return (
        <input
            id={id}
            type={config.kind}
            value={value}
            onChange={e => onChange(e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            className={inputClass}
            placeholder={config.placeholder}
        />
    );
};

const EducationFieldset: React.FC<EducationFieldsetProps> = ({
    context,
    control,
    isSubmitting = false,
    fields,
    append,
    remove
}) => {
    const [newEducation, setNewEducation] = useState<FormEducationEntry>(emptyEducation);

    // TODO: Implementar upload de diploma

    const handleAddEducation = () => {
        if (!newEducation.type) return;
        append({
            ...newEducation,
            id: `education_${Date.now()}`
        });
        setNewEducation(emptyEducation());
    };

    const isCompact = context === 'PROFILE_UPDATE';
    const fieldsetStyle = isCompact ? "border border-gray-200 p-3 rounded-md" : "border border-gray-200 p-4 rounded-md";
    const legendStyle = isCompact ? "text-sm font-medium px-1 text-gray-600" : "text-sm font-medium px-1 text-gray-400";

    // Verificar se o fieldset deve ser exibido usando schema unificado
    if (!collaboratorSchemaHelpers.shouldShowField('educations', context)) {
        return null;
    }

    return (
        <fieldset className={fieldsetStyle}>
            <legend className={legendStyle}>
                Formação Acadêmica
                {collaboratorSchemaHelpers.isFieldRequired('educations', context) && <RequiredIndicator />}
            </legend>

            <div className="space-y-4">
                {/* Lista de Formações Adicionadas */}
                {fields.length > 0 && (
                    <div className="flow-root mt-4">
                        <div className="space-y-4">
                            {fields.map((field, index) => (
                                <div key={field.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                        {educationFields.map(config => (
                                            <div key={config.name}>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {config.label}
                                                </label>
                                                <Controller
                                                    name={`educations.${index}.${config.name}`}
                                                    control={control}
                                                    render={({ field: controllerField }) => (
                                                        <EducationInput
                                                            config={config}
                                                            value={(controllerField.value as string) ?? ''}
                                                            onChange={controllerField.onChange}
                                                            onBlur={controllerField.onBlur}
                                                            disabled={isSubmitting}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Botão Remover */}
                                    <div className="flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => remove(index)}
                                            disabled={isSubmitting}
                                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                                        >
                                            <TrashIcon className="h-4 w-4 mr-1" />
                                            Remover
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Formulário para Adicionar Nova Formação */}
                <div className="border-t border-gray-200 pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-4">Adicionar Nova Formação</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {educationFields.map(config => (
                            <div key={config.name}>
                                <label htmlFor={`new-education-${config.name}`} className="block text-sm font-medium text-gray-700 mb-1">
                                    {config.label} {config.required && <RequiredIndicator />}
                                </label>
                                <EducationInput
                                    id={`new-education-${config.name}`}
                                    config={config}
                                    value={(newEducation[config.name] as string) ?? ''}
                                    onChange={value => setNewEducation(prev => ({ ...prev, [config.name]: value }))}
                                    disabled={isSubmitting}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Botão Adicionar */}
                    <div className="mt-4">
                        <button
                            type="button"
                            onClick={handleAddEducation}
                            disabled={isSubmitting || !newEducation.type}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <PlusCircleIcon className="h-4 w-4 mr-2" />
                            Adicionar Formação
                        </button>
                    </div>
                </div>
            </div>
        </fieldset>
    );
};

export default EducationFieldset;
